"use client";
import { useState, useEffect, useRef } from "react";
import { CaretDown, MapPin as MapPinIcon } from "@phosphor-icons/react";
import { MapPin } from "lucide-react";
import { useI18N } from "common/hooks";

interface SearchLocationProps {
  locationText?: string;
  onChangeLocationClick?: () => void;
  onUseCurrentLocation?: () => void;
  isDefaultLocation?: boolean;
  showIcon?: boolean;
  showLabel?: boolean;
  showUseCurrentLocation?: boolean;
  locationButtonRef?: React.RefObject<HTMLButtonElement>;
  /**
   * `inline` is the original one-line "Find venues in: <place>" row, still what
   * the search page's header shows.
   *
   * `city` is home's, from the DEV-509 frame: a pin, the city in a heading-sized
   * weight with a caret, and an optional second line under it. The two are the
   * same fact in different clothes, so they stay one component rather than two
   * that drift.
   */
  variant?: "inline" | "city";
  /**
   * The `city` variant's second line. Home passes it only when the location was
   * dropped on a map (the address) or is being followed by GPS ("Current
   * location") — @see `ManualLocationSource`.
   */
  citySubtitle?: string;
}

export default function SearchLocation({
  locationText,
  onChangeLocationClick,
  onUseCurrentLocation,
  isDefaultLocation = false,
  showIcon = true,
  showLabel = true,
  showUseCurrentLocation = true,
  locationButtonRef,
  variant = "inline",
  citySubtitle,
}: SearchLocationProps) {
  const i18n = useI18N();
  const [liveMessage, setLiveMessage] = useState("");
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (locationText) {
      setLiveMessage(`${i18n.get("findVenuesIn")} ${locationText}`);
    }
  }, [locationText, i18n]);

  const handleLocationTextClick = () => {
    if (onChangeLocationClick) {
      onChangeLocationClick();
    }
  };

  const handleUseCurrentLocation = () => {
    if (onUseCurrentLocation) {
      onUseCurrentLocation();
    }
  };

  const liveRegion = (
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      {liveMessage}
    </div>
  );

  const place = locationText || i18n.get("defaultLocationOxfordCircus");

  if (variant === "city") {
    return (
      // Figma `15996:206344`: the pin centred on the two lines, 8 between them,
      // both icons filled.
      <div className="flex w-full items-center gap-2">
        {liveRegion}
        <MapPinIcon
          size={32}
          weight="fill"
          aria-hidden="true"
          className="shrink-0 text-fg-neutral-strong"
        />
        <div className="flex min-w-0 flex-col">
          <button
            ref={locationButtonRef}
            type="button"
            onClick={handleLocationTextClick}
            disabled={!onChangeLocationClick}
            /* The caret is decoration; the label says what pressing this does. */
            aria-label={`${i18n.get("changeLocation") || "Change location"}: ${place}`}
            className="flex items-center gap-2 focus-blue w-fit
              title-m-bold text-fg-neutral-strong
              disabled:cursor-default"
          >
            <span className="truncate">{place}</span>
            {onChangeLocationClick && (
              <CaretDown
                size={20}
                weight="fill"
                aria-hidden="true"
                className="shrink-0"
              />
            )}
          </button>
          {citySubtitle && (
            <span className="body-s-regular truncate text-fg-neutral-subtle">
              {citySubtitle}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-nowrap items-center w-full">
      {liveRegion}
      <div className="flex items-center gap-1 min-w-0">
        {showIcon && (
          <MapPin size={20} className="text-fg-neutral-strong flex-shrink-0" />
        )}
        {showLabel && (
          <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
            {i18n.get("findVenuesIn")}
          </span>
        )}
        {/* Underline lives on the button, not here: without a click handler this
            renders as plain text and must not look like a link. */}
        <span className="label-m-medium text-fg-neutral-strong">
          {onChangeLocationClick ? (
            <button
              ref={locationButtonRef}
              type="button"
              onClick={handleLocationTextClick}
              className="transition-colors focus-blue underline"
              aria-label={`${i18n.get("changeLocation") || "Change location"}: ${locationText || i18n.get("defaultLocationOxfordCircus")}`}
            >
              {locationText || i18n.get("defaultLocationOxfordCircus")}
            </button>
          ) : (
            locationText || i18n.get("defaultLocationOxfordCircus")
          )}
          {isDefaultLocation && ` ${i18n.get("isSetAsDefault")}`}
        </span>
      </div>
      {onUseCurrentLocation && showUseCurrentLocation && !isDefaultLocation && (
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          className="ml-auto
          underline
          transition-colors
          focus-blue
          whitespace-nowrap
          flex-shrink-0
          label-m-medium
          text-fg-brand-subtle
          hover:text-fg-brand-strong
          active:text-fg-neutral-strong
          "
          aria-label={i18n.get("resetToCurrentLocation") || "Reset to current location"}
        >
          {i18n.get("resetToCurrentLocation")}
        </button>
      )}
    </div>
  );
}
